import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tag } from 'features/common/types';
import { getTagId } from 'features/common/utils/tagUtils';
import { useAutoFocusWhenVisible } from 'features/common/hooks/useAutoFocusWhenVisible';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import {
	PunchupCombobox,
	ComboboxOption,
} from 'features/common/components/Punchup/primitives/forms/pickers/PunchupCombobox';
import { PunchupTag } from 'features/common/components/Punchup/primitives/display/PunchupTag';
import {
	PunchupColorPicker,
	PRESET_COLORS,
} from 'features/common/components/Punchup/primitives/forms/pickers/PunchupColorPicker';
import { PunchupFieldLabel } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFieldLabel';
import { chipColor } from 'features/common/components/Punchup/primitives/colorUtils';

import styles from './PunchupTagManager.module.css';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Name / description / color payload for both creating and updating a tag. */
export interface TagFormData {
	name: string;
	description?: string;
	color: string;
}

/** Create and update share the same shape; aliases kept for call-site readability. */
export type CreateTagData = TagFormData;
export type UpdateTagData = TagFormData;

export interface PunchupTagManagerProps {
	/** All available tags of the relevant type (pre-filtered by tag_type). */
	availableTags: Tag[];
	/** Tags currently assigned to this entity. */
	selectedTags: Tag[];
	/** Called immediately when the user selects a tag from the search dropdown. */
	onAdd: (tagId: number) => Promise<void>;
	/**
	 * Called when the user clicks Delete in the edit state.
	 * Removes the tag from the entity (deassign — does not delete the tag globally).
	 */
	onRemove: (tagId: number) => Promise<void>;
	/**
	 * Called when the user saves a new tag in create state.
	 * Must return the newly created Tag — it is immediately added to the selected list.
	 */
	onCreate: (data: CreateTagData) => Promise<Tag>;
	/** Called when the user saves changes to an existing tag's name/description/color. */
	onUpdate?: (tagId: number, data: UpdateTagData) => Promise<void>;
	/** Maximum number of tags that may be assigned. Defaults to 10. */
	maxItems?: number;
	/** Override the search bar placeholder. Defaults to 'Search among the existing tags'. */
	searchPlaceholder?: string;
	/**
	 * Called whenever the internal edit/create form transitions between dirty and clean.
	 * Dirty = the form is open and has values that differ from the tag being edited.
	 * Use this to propagate dirty state up to an accordion section's setDirty.
	 */
	onDirtyChange?: (isDirty: boolean) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

type Mode = 'default' | 'editTag';

/**
 * Fallback chip colour for a tag whose `color` is unset. Mirrors the brand primary
 * (`--punchup-color-primary`); kept here as a JS literal because it is passed to JS
 * props (chip `background`, combobox swatch), not into a CSS file where `var()` works.
 */
const DEFAULT_TAG_COLOR = '#FF5E2A';

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * PunchupTagManager
 *
 * Two states:
 *
 *  default  — chip strip of assigned tags (each a clickable button that opens edit),
 *             search bar to find and immediately add unassigned tags, and a
 *             "Create Tag" button (bottom-right) that opens the create form.
 *
 *  editTag  — name / description / color form. Handles both create (editingTag=null)
 *             and edit (editingTag=Tag). Delete (bottom-left) removes the tag from
 *             the entity and is only shown when editing an existing tag.
 *
 * All server calls are handled by the parent via callbacks; this component is API-agnostic.
 */
export const PunchupTagManager: FC<PunchupTagManagerProps> = ({
	availableTags,
	selectedTags,
	onAdd,
	onRemove,
	onCreate,
	onUpdate,
	maxItems = 10,
	searchPlaceholder,
	onDirtyChange,
}) => {
	const { t } = useTranslation();

	// ── Mode state ───────────────────────────────────────────────────────────────

	const [mode, setMode] = useState<Mode>('default');
	const [editingTag, setEditingTag] = useState<Tag | null>(null);

	// ── Optimistic selected ───────────────────────────────────────────────────────

	// Mirrors selectedTags but updates immediately on add/remove/create/update so the
	// UI reflects changes before the parent query refetches.
	const [optimisticSelected, setOptimisticSelected] = useState<Tag[]>(selectedTags);
	useEffect(() => {
		setOptimisticSelected(selectedTags);
	}, [selectedTags]);

	// Tags created this session that may not yet appear in availableTags (pre-refetch)
	const [newlyCreated, setNewlyCreated] = useState<Tag[]>([]);

	// ── Add-in-flight guard ────────────────────────────────────────────────────────
	// Search + dropdown positioning (incl. iframe-safe placement) now live inside
	// PunchupCombobox. We only need a concurrency guard so a double-click can't fire two adds.
	const [isAdding, setIsAdding] = useState<number | null>(null);

	// ── Edit/create form state ───────────────────────────────────────────────────

	const [formName, setFormName] = useState('');
	const [formDesc, setFormDesc] = useState('');
	const [formColor, setFormColor] = useState<string>(PRESET_COLORS[0]);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Ref to the create/edit form's Name field — focused imperatively after the mode swap (see the
	// focus effect below) rather than via a static `autoFocus`.
	const nameInputRef = useRef<HTMLInputElement>(null);

	// ── Dirty tracking for accordion sections ────────────────────────────────────
	// Notify the parent when the edit/create form has uncommitted values.
	// Dirty = form is open AND values differ from the tag being edited (or any value filled for create).
	useEffect(() => {
		if (mode !== 'editTag') {
			onDirtyChange?.(false);
			return;
		}
		const originalName = editingTag?.name ?? '';
		const originalDesc = editingTag?.description ?? '';
		const originalColor = editingTag?.color ?? PRESET_COLORS[0];
		const isDirty = formName !== originalName || formDesc !== originalDesc || formColor !== originalColor;
		onDirtyChange?.(isDirty);
	}, [mode, editingTag, formName, formDesc, formColor, onDirtyChange]);

	// Focus the Name field when the create/edit form opens — deferred past a frame so the iframe
	// height re-anchor can't steal it (the shared iframe-safe focus pattern; replaces a raw autoFocus).
	useAutoFocusWhenVisible(nameInputRef, mode === 'editTag');

	// ── Computed values ──────────────────────────────────────────────────────────

	const allAvailable = useMemo(() => {
		const knownIds = new Set(availableTags.map(getTagId));
		return [...availableTags, ...newlyCreated.filter((t) => !knownIds.has(getTagId(t)))];
	}, [availableTags, newlyCreated]);

	const canAdd = optimisticSelected.length < maxItems;

	const unassigned = useMemo(() => {
		const current = new Set(optimisticSelected.map(getTagId));
		return allAvailable.filter((t) => !current.has(getTagId(t)));
	}, [allAvailable, optimisticSelected]);

	// Combobox options: the colour swatch uses chipColor() so it matches exactly how the
	// PunchupTag chip renders the same tag (light hues darkened for visibility on white) —
	// otherwise a raw light hex reads as a near-invisible dot.
	const unassignedOptions = useMemo<ComboboxOption[]>(
		() =>
			unassigned.map((tag) => ({
				value: getTagId(tag),
				label: tag.name,
				color: chipColor(tag.color || DEFAULT_TAG_COLOR),
				description: tag.description,
			})),
		[unassigned]
	);

	// ── Handlers ─────────────────────────────────────────────────────────────────

	const openEdit = (tag: Tag | null) => {
		setEditingTag(tag);
		setFormName(tag?.name ?? '');
		setFormDesc(tag?.description ?? '');
		setFormColor(tag?.color ?? PRESET_COLORS[0]);
		setMode('editTag');
	};

	const handleAdd = async (tag: Tag) => {
		if (!canAdd || isAdding !== null) return;
		setIsAdding(getTagId(tag));
		try {
			await onAdd(getTagId(tag));
			setOptimisticSelected((prev) => [...prev, tag]);
		} finally {
			setIsAdding(null);
		}
	};

	// PunchupCombobox hands back the selected option's value (the tag id); resolve it to the
	// Tag before delegating to handleAdd.
	const handleAddOption = (tagId: string | number) => {
		const tag = unassigned.find((t) => getTagId(t) === tagId);
		if (tag) handleAdd(tag);
	};

	const handleSave = async () => {
		if (!formName.trim() || isSaving) return;
		setIsSaving(true);
		try {
			if (editingTag === null) {
				const newTag = await onCreate({
					name: formName.trim(),
					description: formDesc.trim() || undefined,
					color: formColor,
				});
				setNewlyCreated((prev) => [...prev, newTag]);
				setOptimisticSelected((prev) => [...prev, newTag]);
			} else if (onUpdate) {
				await onUpdate(getTagId(editingTag), {
					name: formName.trim(),
					description: formDesc.trim() || undefined,
					color: formColor,
				});
				setOptimisticSelected((prev) =>
					prev.map((t) =>
						getTagId(t) === getTagId(editingTag)
							? { ...t, name: formName.trim(), description: formDesc.trim() || undefined, color: formColor }
							: t
					)
				);
			}
			setMode('default');
		} finally {
			setIsSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!editingTag || isDeleting) return;
		setIsDeleting(true);
		try {
			await onRemove(getTagId(editingTag));
			setOptimisticSelected((prev) => prev.filter((t) => getTagId(t) !== getTagId(editingTag)));
			setMode('default');
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDirectRemove = async (tag: Tag) => {
		if (isAdding !== null || isDeleting) return;
		try {
			await onRemove(getTagId(tag));
			setOptimisticSelected((prev) => prev.filter((t) => getTagId(t) !== getTagId(tag)));
		} catch {
			// parent query refetch will reconcile
		}
	};

	// ── Edit / Create state ───────────────────────────────────────────────────────

	if (mode === 'editTag') {
		return (
			<div className={styles.manager}>
				<div className={styles.createForm}>
					<div className={styles.formField}>
						<label className={styles.formLabel}>
							{t('tags-name', 'Name')} <span className={styles.required}>*</span>
						</label>
						<input
							ref={nameInputRef}
							className={styles.formInput}
							type="text"
							placeholder={t('tags-name-placeholder', 'Tag name')}
							value={formName}
							onChange={(e) => setFormName(e.target.value)}
							maxLength={50}
						/>
					</div>

					<div className={styles.formField}>
						<label className={styles.formLabel}>{t('tags-description-green', 'Description')}</label>
						<textarea
							className={styles.formTextarea}
							placeholder={t('tags-description-placeholder', 'Brief description')}
							value={formDesc}
							onChange={(e) => setFormDesc(e.target.value)}
							maxLength={200}
						/>
					</div>

					<div className={styles.formField}>
						<PunchupFieldLabel
							as="span"
							label={t('tags-color', 'Tag color')}
							help={t(
								'tags-color-help',
								"A color to help this tag stand out wherever it appears. It's only used for display."
							)}
						/>
						<PunchupColorPicker value={formColor} onChange={setFormColor} />
					</div>
				</div>

				{editingTag !== null && (
					<p className={styles.scopeNote}>
						{t(
							'tags-edit-scope-note',
							'Editing the name or color changes this tag on every show that uses it. "Remove from show" only un-links it here.'
						)}
					</p>
				)}

				<div className={styles.actions}>
					{editingTag !== null && (
						<PunchupButton
							type="button"
							variant="destructive"
							text={t('tags-remove-from-show', 'Remove from show')}
							onClick={handleDelete}
							disabled={isDeleting}
						/>
					)}
					<div className={styles.actionsRight}>
						<PunchupButton
							type="button"
							variant="secondary"
							text={t('button-cancel', 'Cancel')}
							onClick={() => setMode('default')}
						/>
						<PunchupButton
							type="button"
							variant="primary"
							text={t('button-save', 'Save')}
							onClick={handleSave}
							disabled={!formName.trim() || isSaving}
						/>
					</div>
				</div>
			</div>
		);
	}

	// ── Default state ─────────────────────────────────────────────────────────────

	return (
		<div className={styles.manager}>
			{optimisticSelected.length > 0 ? (
				<div className={styles.tagGrid}>
					{optimisticSelected.map((tag) => (
						<PunchupTag
							key={getTagId(tag)}
							label={tag.name}
							title={t('tags-edit-tag', 'Edit tag')}
							background={tag.color || DEFAULT_TAG_COLOR}
							onClick={() => openEdit(tag)}
							onRemove={() => handleDirectRemove(tag)}
						/>
					))}
				</div>
			) : (
				<p className={styles.empty}>{t('tags-no-tags-assigned', 'No tags assigned')}</p>
			)}

			{canAdd ? (
				<>
					{optimisticSelected.length > 0 && (
						<p className={styles.headroom}>
							{t('tags-count-of-max', '{{count}} of {{max}} tags assigned', {
								count: optimisticSelected.length,
								max: maxItems,
							})}
						</p>
					)}

					<PunchupCombobox
						options={unassignedOptions}
						value={null}
						onChange={handleAddOption}
						keepOpenOnSelect
						placeholder={searchPlaceholder ?? t('tags-add-placeholder', 'Add a tag')}
						searchPlaceholder={searchPlaceholder ?? t('tags-search-placeholder-green', 'Search tags…')}
						emptyLabel={t('tags-no-tags-found', 'No matching tags')}
					/>

					<div className={styles.actionsRight}>
						<PunchupButton
							type="button"
							variant="outline"
							text={t('tags-create-new', 'Create Tag')}
							onClick={() => openEdit(null)}
						/>
					</div>
				</>
			) : (
				<p className={styles.limitNote}>
					{t('tags-max-limit-reached-green', 'Maximum of {{max}} tags reached', { max: maxItems })}
				</p>
			)}
		</div>
	);
};
